import {
   AuthSession,
   CookieDetails,
   RefreshTokenReq,
   SessionDetails,
   InvalidAuthSessionError
} from '@flowwai-frontend/shared/auth/common'
import {
   getCookie,
   setCookie,
   removeCookie
} from '@flowwai-frontend/shared/utils/web'

class CookieAuthSession implements AuthSession {
   private accessTokenPromise!: Promise<SessionDetails> | null
   private cookieDetails!: CookieDetails

   constructor(cookieDetails: CookieDetails, sessionDetails?: SessionDetails) {
      this.cookieDetails = cookieDetails

      this.init()
      sessionDetails && this.setSessionDetails(sessionDetails)
   }

   private init = (): void => {
      this.accessTokenPromise = null
   }

   private getAccessTokenWithoutExpiry = (): string | undefined =>
      getCookie(this.cookieDetails.accessTokenKeyName)

   private getRefreshToken = (): string | undefined =>
      getCookie(this.cookieDetails.refreshTokenKeyName)

   private getAccessTokenKeyNameWithExpiry = (): string =>
      `${this.cookieDetails.accessTokenKeyName}31393378657035265`

   private getAccessTokenWithExpiry = (): string | undefined =>
      getCookie(this.getAccessTokenKeyNameWithExpiry())

   getAccessToken = async (): Promise<string> => {
      const accessTokenWithExpiry = this.getAccessTokenWithExpiry()

      const refreshToken = this.getRefreshToken()

      const accessTokenWithoutExpiry = this.getAccessTokenWithoutExpiry()

      if (accessTokenWithExpiry) return accessTokenWithExpiry
      else if (refreshToken) {
         if (accessTokenWithoutExpiry) {
            if (this.accessTokenPromise) {
               await this.setSessionDetailsFromPendingPromise(
                  this.accessTokenPromise
               )
            } else {
               await this.setSessionDetailsFromRefreshAPI(
                  accessTokenWithoutExpiry,
                  refreshToken
               )
            }
            const accessTokenWithExpiry = this.getAccessTokenWithExpiry()

            if (accessTokenWithExpiry) return accessTokenWithExpiry
         }
      }
      this.clearSessionDetails()
      throw new InvalidAuthSessionError()
   }
   private clearSessionDetailsAndThrowError = (): void => {
      this.clearSessionDetails()
      throw new InvalidAuthSessionError()
   }

   private setSessionDetailsFromRefreshAPI = async (
      accessTokenWithoutExpiry: string,
      refreshToken: string
   ): Promise<void> => {
      try {
         const req: RefreshTokenReq = {
            accessToken: accessTokenWithoutExpiry,
            refreshToken: refreshToken
         }

         this.accessTokenPromise = this.cookieDetails.refreshTokensAPI(req)

         const sessionDetails = await this.accessTokenPromise
         this.setSessionDetails(sessionDetails)

         this.accessTokenPromise = null
      } catch (error) {
         this.clearSessionDetailsAndThrowError()
      }
   }

   private setSessionDetailsFromPendingPromise = async (
      accessTokenPromise: Promise<SessionDetails>
   ): Promise<void> => {
      try {
         const sessionDetails = await accessTokenPromise

         this.setSessionDetails(sessionDetails)
         this.accessTokenPromise = null
      } catch (error) {
         this.clearSessionDetailsAndThrowError()
      }
   }

   private setAccessTokenWithExpiry = (
      sessionDetails: SessionDetails
   ): void => {
      setCookie(
         this.getAccessTokenKeyNameWithExpiry(),
         sessionDetails.accessToken,
         sessionDetails.expiredInDays
      )
   }

   private setAccessTokenWithoutExpiry = (accessToken: string): void => {
      setCookie(this.cookieDetails.accessTokenKeyName, accessToken)
   }

   private setRefreshToken = (refreshToken: string): void => {
      setCookie(this.cookieDetails.refreshTokenKeyName, refreshToken)
   }

   setSessionDetails = (sessionDetails: SessionDetails): void => {
      this.setAccessTokenWithExpiry(sessionDetails)
      this.setAccessTokenWithoutExpiry(sessionDetails.accessToken)
      this.setRefreshToken(sessionDetails.refreshToken)
   }

   clearSessionDetails = (): void => {
      removeCookie(this.cookieDetails.accessTokenKeyName)
      removeCookie(this.cookieDetails.refreshTokenKeyName)
      removeCookie(this.getAccessTokenKeyNameWithExpiry())
   }

   clearAccessToken = (): void => {
      removeCookie(this.getAccessTokenKeyNameWithExpiry())
   }
}

export { CookieAuthSession }
